import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandLogo } from '../lib/branding';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { LogOut, User, Settings, Search, SlidersHorizontal, Home } from 'lucide-react';
import { MagnifyingGlass, UserCircle, GearSix, SignOut, House, ShoppingCart } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import DashboardNav from './DashboardNav';
import UnifiedFilters from './UnifiedFilters';
import OrgSwitcher from './OrgSwitcher';

const DashboardHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFiltersDialog, setShowFiltersDialog] = useState(false);
  const [filters, setFilters] = useState({
    partners: [],
    editeurs: [],
    categories: [],
    sous_categories: [],
    temps_moyen_partie: '',
    nb_joueurs_min: '',
    nb_joueurs_max: '',
    age_minimum: '',
  });

  const handleLogout = () => {
    logout();
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.prenom_utilisateur?.[0] || ''}${user.nom_utilisateur?.[0] || ''}`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Build query params from search and filters
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (filters.partners?.length > 0) params.append('partners', filters.partners.join(','));
    if (filters.editeurs?.length > 0) params.append('editeurs', filters.editeurs.join(','));
    if (filters.categories?.length > 0) params.append('categories', filters.categories.join(','));
    if (filters.sous_categories?.length > 0) params.append('sous_categories', filters.sous_categories.join(','));
    if (filters.temps_moyen_partie) params.append('temps_moyen_partie', filters.temps_moyen_partie);
    if (filters.nb_joueurs_min) params.append('nb_joueurs_min', filters.nb_joueurs_min);
    if (filters.nb_joueurs_max) params.append('nb_joueurs_max', filters.nb_joueurs_max);
    if (filters.age_minimum) params.append('age_minimum', filters.age_minimum);
    
    // Navigate to search page with query (Phase 3.4.3 — handle empty search)
    const trimmed = searchQuery.trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate('/search');
    }
    setSearchQuery(''); // Reset input after navigation
  };

  const handleApplyFilters = () => {
    setShowFiltersDialog(false);
    handleSearch({ preventDefault: () => {} });
  };

  const handleResetFilters = () => {
    setFilters({
      partners: [],
      editeurs: [],
      categories: [],
      sous_categories: [],
      temps_moyen_partie: '',
      nb_joueurs_min: '',
      nb_joueurs_max: '',
      age_minimum: '',
    });
  };

  return (
    <div className="sticky top-0 z-50 w-full">
      {/* Top Header - Glassmorphism */}
      <div className="ludo-glass">
        <div className="flex h-16 items-center px-6 gap-6 max-w-[1400px] mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <BrandLogo size="lg" />
            </Link>
          </div>

          {/* Search Bar - Pill style - Hidden for Admin */}
          {user?.role !== 'admin' && (
            <div className="flex-1 max-w-2xl mx-auto">
              <form onSubmit={handleSearch} className="flex gap-2 items-center">
                <div className="flex-1 relative ludo-search-pill flex items-center">
                  <MagnifyingGlass className="absolute left-4 h-5 w-5" style={{ color: 'var(--ludo-text-secondary)' }} weight="bold" />
                  <input
                    placeholder="Rechercher un jeu, un éditeur, un distributeur..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 h-10 bg-transparent text-sm focus:outline-none"
                    style={{ color: 'var(--ludo-text)' }}
                    data-testid="header-search-input"
                  />
                  <button type="submit" className="absolute right-2 w-7 h-7 rounded-full flex items-center justify-center transition-colors bg-primary text-white" data-testid="search-button">
                    <Search className="h-3.5 w-3.5" />
                  </button>
                </div>
                {/* Bouton Recherche avancée (Phase 3.4.3) */}
                <button
                  type="button"
                  onClick={() => navigate('/search')}
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-gray-100"
                  style={{ color: 'var(--ludo-text-secondary)' }}
                  title="Recherche avancée"
                  data-testid="advanced-search-button"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </button>
              </form>
            </div>
          )}
          
          {/* Spacer for admin */}
          {user?.role === 'admin' && <div className="flex-1"></div>}

          {/* Org Switcher — visible uniquement si ≥ 2 orgs actives */}
          <OrgSwitcher />

          {/* User Menu */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="text-right hidden md:block">
              <p className="text-sm font-semibold" style={{ color: 'var(--ludo-text)' }}>{user?.nom_magasin || user?.pseudo || 'Utilisateur'}</p>
              <p className="text-xs capitalize" style={{ color: 'var(--ludo-text-secondary)' }}>{user?.role === 'partenaire' ? 'Partenaire' : user?.role || 'Magasin'}</p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full border-2 transition-colors hover:border-primary" style={{ borderColor: 'var(--ludo-border)' }} data-testid="user-menu-button">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="text-sm font-bold text-white bg-primary">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-heading">Mon Compte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/personal-space')} data-testid="personal-space-menu-item">
                  <House className="mr-2 h-4 w-4" weight="duotone" />
                  Mon Espace personnel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/profil')} data-testid="profile-menu-item">
                  <UserCircle className="mr-2 h-4 w-4" weight="duotone" />
                  Mon Compte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} data-testid="settings-menu-item">
                  <GearSix className="mr-2 h-4 w-4" weight="duotone" />
                  Paramètres
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="logout-menu-item">
                  <SignOut className="mr-2 h-4 w-4" weight="duotone" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Navigation Menu */}
      <DashboardNav />
    </div>
  );
};

export default DashboardHeader;
